import { useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useSubscriptionTier } from './use-subscription-tier'
import { useBillingCapabilities } from '@/features/subscription/billing/billing-capabilities'
import { useIsMockedFreeTest } from '@/features/subscription/billing/dev/billing-mock-state'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import type { ConversionTriggerSource } from 'api/dist/types/posthog-events'

/**
 * Page-level blocker: on mount, if the user is in free test,
 * opens the billing plans modal. Closable — when dismissed, redirects
 * to the default route (e.g. agent home). Use on pages that free test
 * users should not access directly.
 */
export function useFreeTestPageBlocker(title: string, source: ConversionTriggerSource, redirectTo?: string) {
    const { tier } = useSubscriptionTier()
    const { canShowStripeUpsellModal } = useBillingCapabilities()
    const isSuperUser = useIsSuperUser()
    // The FREE_TEST billing mock previews the blocker as a super user — neutralize the two
    // exemptions (super users, non-Stripe orgs) so the mocked tier actually bites.
    const isMockedFreeTest = useIsMockedFreeTest()
    const router = useRouter()

    useEffect(() => {
        if (!canShowStripeUpsellModal && !isMockedFreeTest) return
        if (tier === 'free_test' && !(isSuperUser && !isMockedFreeTest)) {
            window.onSubscriptionError?.('USAGE_LIMIT_REACHED_IN_FREE_TEST', {
                title,
                billingDetails: { feature: 'credits' },
                source,
                onClose: () => {
                    if (redirectTo) {
                        router.push(redirectTo)
                    } else {
                        router.back()
                    }
                },
            })
        }
    }, [tier, title, source, redirectTo, router, isSuperUser, canShowStripeUpsellModal, isMockedFreeTest])
}

/**
 * CTA-level guard: returns a function that checks if the user is in free test.
 * If so, opens the billing plans modal (closable) and returns true (blocked).
 * If not, returns false (proceed with navigation).
 *
 * Usage:
 * ```
 * const guard = useFreeTestGuard('Start a free trial to create custom characters', 'create_custom_character_cta')
 * const handleClick = () => {
 *     if (guard()) return
 *     router.push(...)
 * }
 * ```
 */
export function useFreeTestGuard(title: string, source: ConversionTriggerSource) {
    const { tier } = useSubscriptionTier()
    const { canShowStripeUpsellModal } = useBillingCapabilities()
    const isSuperUser = useIsSuperUser()
    // See useFreeTestPageBlocker — same exemption-neutralizing for the FREE_TEST mock.
    const isMockedFreeTest = useIsMockedFreeTest()

    return useCallback(() => {
        if (!canShowStripeUpsellModal && !isMockedFreeTest) return false
        if (tier !== 'free_test' || (isSuperUser && !isMockedFreeTest)) return false
        window.onSubscriptionError?.('USAGE_LIMIT_REACHED_IN_FREE_TEST', {
            title,
            billingDetails: { feature: 'credits' },
            source,
        })
        return true
    }, [tier, title, source, isSuperUser, canShowStripeUpsellModal, isMockedFreeTest])
}
